// frontend/src/samkalp-faculty/liveSessions/SessionDetailPanel.js
//
// Inline detail/management panel for one session: lifecycle actions
// (go-live/end/cancel), the Q&A console (answer pending questions first),
// and the CSAT feedback summary once completed.
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Form, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
    goLiveSession, endLiveSession, cancelLiveSession,
    listQuestionsFaculty, answerQuestion, getFeedbackSummaryFaculty,
    getAttendanceSummaryFaculty, getRecordingUrlFaculty,
} from '../../api/liveSessionsApi';

const STATUS_VARIANT = { scheduled: 'secondary', live: 'danger', completed: 'success', cancelled: 'dark' };

const SessionDetailPanel = ({ slug, session, onChanged }) => {
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [attendance, setAttendance] = useState(null);
    const [answerDrafts, setAnswerDrafts] = useState({});
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [recordingUrl, setRecordingUrl] = useState(null);

    const loadQuestions = useCallback(() => {
        listQuestionsFaculty(slug, session.id).then(setQuestions).catch(() => {});
    }, [slug, session.id]);

    const loadAttendance = useCallback(() => {
        getAttendanceSummaryFaculty(slug, session.id).then(setAttendance).catch(() => {});
    }, [slug, session.id]);

    useEffect(() => {
        loadQuestions();
        if (session.status === 'live' || session.status === 'completed') {
            loadAttendance();
        }
        if (session.status === 'completed') {
            getFeedbackSummaryFaculty(slug, session.id).then(setFeedback).catch(() => {});
            if (session.recording_status === 'available') {
                getRecordingUrlFaculty(slug, session.id).then(r => setRecordingUrl(r.url)).catch(() => {});
            }
        }
        if (session.status !== 'live') return undefined;
        const interval = setInterval(() => { loadQuestions(); loadAttendance(); }, 8000);
        return () => clearInterval(interval);
    }, [slug, session.id, session.status, session.recording_status, loadQuestions, loadAttendance]);

    const runAction = async (fn) => {
        setError(''); setBusy(true);
        try {
            const updated = await fn();
            onChanged && onChanged(updated);
        } catch (err) {
            setError(err.response?.data?.detail || 'Action failed.');
        } finally {
            setBusy(false);
        }
    };

    const canGoLive = session.status === 'scheduled' && new Date() >= new Date(session.go_live_opens_at);
    const goLiveNotYet = session.status === 'scheduled' && new Date() < new Date(session.go_live_opens_at);

    const submitAnswer = async (questionId) => {
        const answer_text = answerDrafts[questionId];
        if (!answer_text) return;
        await answerQuestion(slug, session.id, questionId, { answer_text });
        setAnswerDrafts(prev => ({ ...prev, [questionId]: '' }));
        loadQuestions();
    };

    return (
        <div className="session-detail-panel">
            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

            <div className="d-flex align-items-center gap-2 mb-3">
                <Badge bg={STATUS_VARIANT[session.status] || 'secondary'} className="text-uppercase">{session.status}</Badge>
                {goLiveNotYet && <span className="small text-muted">Go-live opens at {new Date(session.go_live_opens_at).toLocaleString()}</span>}
                {attendance && (
                    <span className="small text-muted">
                        · {attendance.attendee_count} student{attendance.attendee_count === 1 ? '' : 's'} attended
                        {attendance.attendee_count > 0 ? ` (avg ${attendance.average_minutes} min)` : ''}
                    </span>
                )}
            </div>

            <div className="d-flex flex-wrap gap-2 mb-4">
                {session.status === 'scheduled' && (
                    <>
                        <Button size="sm" variant="danger" disabled={!canGoLive || busy} onClick={() => runAction(() => goLiveSession(slug, session.id).then(r => ({ ...session, status: 'live', ...r })))}>
                            {busy ? <Spinner size="sm" animation="border" /> : 'Go Live'}
                        </Button>
                        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => runAction(() => cancelLiveSession(slug, session.id).then(() => ({ ...session, status: 'cancelled' })))}>
                            Cancel
                        </Button>
                    </>
                )}
                {session.status === 'live' && (
                    <>
                        <Button size="sm" variant="dark" onClick={() => navigate(`/samkalp-ias/faculty/live/${session.id}/host`)}>
                            Open Broadcast Console
                        </Button>
                        <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => runAction(() => endLiveSession(slug, session.id))}>
                            End Session
                        </Button>
                    </>
                )}
            </div>

            <h6 className="fw-bold">Q&amp;A</h6>
            {questions.length === 0 && <p className="small text-muted">No questions yet.</p>}
            <ListGroup className="mb-4">
                {questions.map(q => (
                    <ListGroup.Item key={q.id}>
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <div className="small text-muted">{q.student_name || 'Student'}</div>
                                <strong className="small">{q.question_text}</strong>
                            </div>
                            <Badge bg={q.status === 'answered' ? 'success' : 'warning'} text={q.status === 'answered' ? undefined : 'dark'}>{q.status}</Badge>
                        </div>
                        {q.status === 'answered' ? (
                            <p className="small text-muted mb-0 mt-1">{q.answer_text}</p>
                        ) : (
                            <div className="d-flex gap-2 mt-2">
                                <Form.Control
                                    size="sm"
                                    placeholder="Type an answer..."
                                    value={answerDrafts[q.id] || ''}
                                    onChange={e => setAnswerDrafts(prev => ({ ...prev, [q.id]: e.target.value }))}
                                />
                                <Button size="sm" onClick={() => submitAnswer(q.id)}>Answer</Button>
                            </div>
                        )}
                    </ListGroup.Item>
                ))}
            </ListGroup>

            {session.status === 'completed' && (
                <>
                    {recordingUrl && (
                        <video controls src={recordingUrl} className="mb-3" style={{ maxWidth: '100%', width: 640, borderRadius: 8, background: '#000', display: 'block' }} />
                    )}
                    {session.notes_generation_status === 'generating' && (
                        <p className="small text-muted mb-3"><Spinner size="sm" animation="border" className="me-1" />Generating lecture notes from the transcript…</p>
                    )}
                    {session.notes_generation_status === 'available' && (
                        <Alert variant="success" className="py-2 small mb-3">
                            Lecture notes generated — find them under this project's Faculty Resources to review and share with students.
                        </Alert>
                    )}
                    {session.notes_generation_status === 'failed' && (
                        <p className="small text-muted mb-3">Lecture notes could not be generated for this session.</p>
                    )}
                    <h6 className="fw-bold">Feedback (CSAT)</h6>
                    {feedback ? (
                        feedback.count === 0 ? (
                            <p className="small text-muted">No feedback submitted yet.</p>
                        ) : (
                            <>
                                <p className="small">Average rating: <strong>{feedback.average_rating} / 5</strong> ({feedback.count} response{feedback.count === 1 ? '' : 's'})</p>
                                <ListGroup>
                                    {feedback.comments.map((c, i) => (
                                        <ListGroup.Item key={i} className="small">★{c.rating} — {c.comments}</ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </>
                        )
                    ) : <Spinner size="sm" animation="border" />}
                </>
            )}
        </div>
    );
};

export default SessionDetailPanel;
